/* Tools for working with ISO 8601 Duration strings,
  for example: P1DT10H20M50S. Reference doc:
  https://www.digi.com/resources/documentation/digidocs//90001488-13/reference/r_iso_8601_duration_format.htm
*/

// Time values in milliseconds (ms)
export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const FIVE_MINUTES = 5 * MINUTE;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;
export const MONTH = 30.5 * DAY;
export const YEAR = 365 * DAY;

export const isoDurationRegex =
  /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/;

export const toISOValue = (duration: DurationObject): string | null => {
  const { days, hours, minutes, seconds } = duration;

  // Tiny hack: as we translate 'null' to all 0s, we do it the other
  // way around too.
  if (!days && !hours && !minutes && !seconds) {
    return null;
  }

  return (
    "P" +
    days +
    "DT" +
    (hours < 10 ? "0" : "") +
    hours +
    "H" +
    (minutes < 10 ? "0" : "") +
    minutes +
    "M" +
    (seconds < 10 ? "0" : "") +
    seconds +
    "S"
  );
};

export const fromISOValue = (value: string | null): DurationObject => {
  // Translate a string of the form 'P1DT10H20M50S' to an object.
  if (value) {
    const match = value.match(isoDurationRegex);

    if (match) {
      return {
        years: parseFloat(match[1]) || 0,
        months: parseFloat(match[2]) || 0,
        days: parseFloat(match[3]) || 0,
        hours: parseFloat(match[4]) || 0,
        minutes: parseFloat(match[5]) || 0,
        seconds: parseFloat(match[6]) || 0,
      };
    }
  }

  return {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
};

export const fromISOValueToMs = (value: string | null): number => {
  const durationObject = fromISOValue(value);
  const { years, months, days, hours, minutes, seconds } = durationObject;

  return (
    years * YEAR + months * MONTH + days * DAY + hours * HOUR + minutes * MINUTE + seconds * SECOND
  );
};

export const rasterIntervalStringServerToDurationObject = (str: string): DurationObject => {
  if (str.split(" ")[1]) {
    // "2 03:04:05"
    return {
      years: 0,
      months: 0,
      days: parseInt(str.split(" ")[0]),
      hours: parseInt(str.split(" ")[1].split(":")[0]),
      minutes: parseInt(str.split(" ")[1].split(":")[1]),
      seconds: parseInt(str.split(" ")[1].split(":")[2]),
    };
  } else {
    // "03:04:05"
    return {
      years: 0,
      months: 0,
      days: 0,
      hours: parseInt(str.split(":")[0]),
      minutes: parseInt(str.split(":")[1]),
      seconds: parseInt(str.split(":")[2]),
    };
  }
};

export function fromISOValueToLizardFrequency(isoInterval: string) {
  const durationObject = fromISOValue(isoInterval);
  const { days, hours, minutes, seconds } = durationObject;
  let freq = "";
  // https://pandas.pydata.org/pandas-docs/stable/user_guide/timeseries.html#offset-aliases
  if (days > 0) freq += `${days}D`;
  if (hours > 0) freq += `${hours}H`;
  if (minutes > 0) freq += `${minutes}T`;
  if (seconds > 0) freq += `${seconds}S`;
  return freq;
}

export interface DurationObject {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
